import * as React from "react";
import { useForm, useFieldArray, useWatch, Control } from "react-hook-form";
import {
  Button,
  Grid,
  Title,
  Text,
  Tabs,
  SegmentedControl,
  Modal,
  ActionIcon,
  Flex,
  Stack,
  Divider,
  TextInput,
  Group,
} from "@mantine/core";
import { CTextInput, CMultiSelect, CSelect, CTextarea } from "@supportinitiative/react-hook-form-mantine";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { object, string, number, date, array, setLocale } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { getId } from "../../../../../utilities";
import { useLoaders } from "src/hooks";
import { QuerySetCVMContext } from "../../../QuerySetCVMContext";
import { UAction, URawQueryType } from "@supportinitiative/common";

setLocale({
  mixed: {
    required: "Required",
    notType: "Invalid Value",
  },
});

const RawQueryActionSchema = object({
  id: string().required(),
  name: string().required(),
  type: string().required(),
  description: string().nullable(),
  definition: object({
    loaderId: string().nullable(),
    query: string().required(),
    colums: array().of(
      object({
        name: string().required(),
        props: string().nullable(),
      }),
    ),
  }),
});

function RawQuery({
  save,
  cancel,
  action,
}: {
  cancel: () => void;
  save: (values: any) => void;
  action: UAction<URawQueryType>;
}) {
  const { state: querySetFormState } = React.useContext(QuerySetCVMContext);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<UAction<URawQueryType>>({
    defaultValues: {
      ...action,
      definition: {
        ...action.definition,
      },
    },
    criteriaMode: "all",
    resolver: yupResolver(RawQueryActionSchema),
  });

  const columnsArray = useFieldArray({
    control,
    name: `definition.columns`,
  });

  const { data: loaders } = useLoaders(querySetFormState.entityId);

  return (
    <Modal centered size={"100%"} opened={true} onClose={cancel} title={<Title order={4}>Raw Query</Title>}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          // handleSubmit(onSubmitForm)();
          event.stopPropagation();
          console.log("inside child");
          // cancel();
        }}
        noValidate
      >
        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={6}>
            <CTextInput
              name="name"
              control={control}
              label="Name"
              required
              showErrorText={false}
              disabled={querySetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
          <Grid.Col span={6}>
            <CTextInput name="id" control={control} label="ID" required showErrorText={false} disabled={true} />
          </Grid.Col>
        </Grid>

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CTextarea
              name="description"
              minLength={5}
              control={control}
              label="Description"
              showErrorText={false}
              disabled={querySetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>
        <Divider my="xs" label="Query" />

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CSelect
              name="definition.loaderId"
              label="Loader"
              placeholder="Select Loader"
              control={control}
              data={
                loaders && loaders.length > 0
                  ? loaders.map((loader: any) => {
                      return { label: loader.name, value: loader.id };
                    })
                  : []
              }
              disabled={querySetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Grid align="flex-start" gutter={"xs"} mb="lg" style={{ display: "flex", alignItems: "flex-end" }}>
          <Grid.Col span={12}>
            <CTextarea
              minRows={4}
              autosize
              name="definition.query"
              label="Query"
              placeholder="Query"
              control={control}
              disabled={querySetFormState.actionOptions.disableAll}
            />
          </Grid.Col>
        </Grid>

        <Divider my="xs" label="Display" />

        <Stack gap="0" mb="xs">
          <Group align="center" mt="lg">
            <Text fw={500} style={{ fontSize: "14px" }}>
              Columns
            </Text>
            <ActionIcon
              size="sm"
              variant="white"
              color="gray"
              onClick={() => {
                columnsArray.append({
                  name: "",
                  component: "TEXT",
                  value1: "",
                  value2: "",
                  value3: "",
                  props: "",
                  label: "",
                });
              }}
              disabled={querySetFormState.actionOptions.disableAll}
            >
              <IconPlus />
            </ActionIcon>
          </Group>

          {columnsArray.fields.length === 0 ? (
            <Grid align="stretch" gutter={"xs"} mb="xs">
              <Grid.Col span={12}>
                <Divider label="No columns" mt="xs" labelPosition="left" />
              </Grid.Col>
            </Grid>
          ) : (
            <Stack gap="0" mb="xs">
              <Grid align="stretch" gutter={"xs"} mb="xs">
                <Grid.Col span={12}>
                  {columnsArray.fields.map((field, index) => {
                    return (
                      <Grid align="stretch" gutter={"xs"} mb="xs" key={field.id} columns={24}>
                        <Grid.Col span={3}>
                          <CTextInput
                            name={`definition.columns.${index}.name`}
                            control={control}
                            showErrorText={false}
                            label={index === 0 ? "Name" : ""}
                            required
                            disabled={querySetFormState.actionOptions.disableAll}
                          />
                        </Grid.Col>
                        <Grid.Col span={3}>
                          <CTextInput
                            name={`definition.columns.${index}.label`}
                            control={control}
                            showErrorText={false}
                            label={index === 0 ? "Label" : ""}
                            required
                            disabled={querySetFormState.actionOptions.disableAll}
                          />
                        </Grid.Col>
                        <Grid.Col span={3}>
                          <CSelect
                            name={`definition.columns.${index}.component`}
                            control={control}
                            clearable
                            searchable
                            label={index === 0 ? "Component" : ""}
                            data={[
                              { label: "Badge", value: "BADGE" },
                              { label: "Text", value: "TEXT" },
                              { label: "Links", value: "LINKS" },
                            ]}
                            required
                            disabled={querySetFormState.actionOptions.disableAll}
                          />
                        </Grid.Col>
                        <Grid.Col span={4}>
                          <CTextarea
                            name={`definition.columns.${index}.value1`}
                            control={control}
                            showErrorText={false}
                            label={index === 0 ? "Value 1" : ""}
                            disabled={querySetFormState.actionOptions.disableAll}
                          />
                        </Grid.Col>
                        <Grid.Col span={4}>
                          <CTextarea
                            name={`definition.columns.${index}.value2`}
                            control={control}
                            showErrorText={false}
                            label={index === 0 ? "Value 2" : ""}
                            disabled={querySetFormState.actionOptions.disableAll}
                          />
                        </Grid.Col>
                        <Grid.Col span={4}>
                          <CTextarea
                            name={`definition.columns.${index}.value3`}
                            control={control}
                            showErrorText={false}
                            label={index === 0 ? "Value 3" : ""}
                            disabled={querySetFormState.actionOptions.disableAll}
                          />
                        </Grid.Col>

                        <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
                          <ActionIcon
                            size="sm"
                            variant="white"
                            color="gray"
                            onClick={() => {
                              columnsArray.remove(index);
                            }}
                            disabled={querySetFormState.actionOptions.disableAll}
                          >
                            <IconTrash />
                          </ActionIcon>
                        </Grid.Col>
                      </Grid>
                    );
                  })}
                </Grid.Col>
              </Grid>
            </Stack>
          )}
        </Stack>

        <Grid align="stretch" gutter={"xs"} mb="xs">
          <Grid.Col span={2}>
            <Button
              color="teal"
              variant="filled"
              fullWidth
              type="submit"
              onClick={
                querySetFormState.actionOptions.disableAll
                  ? () => {
                      console.log("only cancel");
                      cancel();
                    }
                  : async (event: React.MouseEvent<HTMLButtonElement>) => {
                      event.preventDefault();
                      const values = getValues();
                      // console.log("values", values);
                      clearErrors();
                      try {
                        await RawQueryActionSchema.validate(values, {
                          abortEarly: false,
                        });
                        console.log("values", values);
                        save?.(values);
                        //cancel();
                      } catch (error) {
                        console.log("errors", error);
                        console.log("errors", JSON.stringify(error));
                        console.log("error inner", JSON.stringify(error));
                        (error as any).inner.forEach((err: any) => {
                          setError(err.path, {
                            type: "custom",
                            message: err.message,
                          });
                        });
                      }
                    }
              }
            >
              Ok
            </Button>
          </Grid.Col>
        </Grid>
      </form>
    </Modal>
  );
}

export default RawQuery;
